// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Badge, Stack, Text, Title } from '@mantine/core';
import type { GuidanceResponse, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  INLAB_REFERRAL_MODULE_URI,
  PATIENT_REFERRAL_STATUS,
  PATIENT_TRIAGE_STATUS,
  POLARIS_REFERRAL_STATUS_TAG_SYSTEM,
  POLARIS_TRIAGE_STATUS_TAG_SYSTEM,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

const STATUS_BADGE_COLORS: Record<string, string> = {
  'n/a': 'gray',
  'ready-to-refer': 'blue',
  referred: 'green',
};

const REFERRAL_STATUS_LABELS: Record<string, string> = {
  'n/a': 'N/A',
  'ready-to-refer': 'Ready to Refer',
  referred: 'Referred',
};

export interface ReferralOutSectionProps {
  patient: Patient;
}

export function ReferralOutSection({ patient }: ReferralOutSectionProps): JSX.Element | null {
  const medplum = useMedplum();
  const [latestGr, setLatestGr] = useState<GuidanceResponse | undefined>(undefined);

  // Get referral status from patient tags
  const referralStatus =
    patient.meta?.tag?.find((t) => t.system === POLARIS_REFERRAL_STATUS_TAG_SYSTEM)?.code ?? PATIENT_REFERRAL_STATUS.na;

  // Get triage status from patient tags
  const triageStatus = patient.meta?.tag?.find((t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM)?.code;

  // Only render if patient has a denied triage status OR a referral status tag
  const shouldRender = triageStatus === PATIENT_TRIAGE_STATUS.denied || referralStatus !== PATIENT_REFERRAL_STATUS.na;

  useEffect(() => {
    if (!patient.id || !shouldRender) {
      return;
    }
    medplum
      .searchResources('GuidanceResponse', {
        subject: `Patient/${patient.id}`,
        _sort: '-_lastUpdated',
        _count: 10,
      })
      .then((grs) => {
        const inlabGr = grs.find((gr) => gr.moduleUri === INLAB_REFERRAL_MODULE_URI);
        setLatestGr(inlabGr);
      })
      .catch((err) => Sentry.captureException(err));
  }, [patient.id, shouldRender, medplum]);

  if (!shouldRender) {
    return null;
  }

  const statusLabel = REFERRAL_STATUS_LABELS[referralStatus] ?? 'N/A';
  const statusColor = STATUS_BADGE_COLORS[referralStatus] ?? 'gray';
  const notesText = latestGr?.note?.[0]?.text ?? 'No notes';
  const hasNotes = latestGr?.note?.[0]?.text !== undefined;

  return (
    <Stack gap="md" p="md" component="section">
      <Title order={5}>In-Lab Referral</Title>
      <Stack gap="xs">
        <Badge color={statusColor}>{statusLabel}</Badge>
        <Text size="sm" c={hasNotes ? undefined : 'dimmed'}>
          {notesText}
        </Text>
        {latestGr?.occurrenceDateTime && (
          <Text size="xs" c="dimmed">
            Last updated: {new Date(latestGr.occurrenceDateTime).toLocaleDateString()}
          </Text>
        )}
      </Stack>
    </Stack>
  );
}
